import { GraphError, type BatchRequest, type BatchResponse, type GraphClient } from "./graph-client";

/**
 * Cartelle di una raccolta documenti, per id (non per percorso):
 * navigazione, ricerca, creazione e ricostruzione di una struttura modello.
 *
 * Gli id restano validi se le cartelle vengono rinominate o spostate; i percorsi no.
 */

export interface FolderItem {
  id: string;
  name: string;
  webUrl: string;
  /** Percorso relativo alla radice della raccolta ("" = radice). */
  path: string;
  /** Numero di elementi contenuti (cartelle + file), come riportato da Graph. */
  childCount: number;
}

/** File di una raccolta. */
export interface FileItem {
  id: string;
  name: string;
  webUrl: string;
  path: string;
  size?: number;
  modifiedAt?: string;
}

interface RawItem {
  id: string;
  name: string;
  webUrl: string;
  size?: number;
  lastModifiedDateTime?: string;
  folder?: { childCount: number };
  file?: object;
  root?: object;
  parentReference?: { driveId?: string; id?: string; path?: string };
}

const SELECT = "id,name,webUrl,folder,file,root,parentReference";

/** Cartella del modello: solo il nome e le sottocartelle (i file vengono ignorati). */
export interface TemplateNode {
  name: string;
  children: TemplateNode[];
}

export interface BuildProgress {
  done: number;
  total: number;
}

export interface BuildResult {
  root: FolderItem;
  /** Cartelle create in questa esecuzione (radice compresa). */
  created: number;
  /** Cartelle già presenti e riusate (esecuzione ripetuta dopo un errore). */
  reused: number;
}

/** Esiste già un elemento con lo stesso nome nella cartella di destinazione. */
export class FolderExistsError extends Error {
  constructor(
    readonly folderName: string,
    readonly existing?: FolderItem,
  ) {
    super(`Esiste già una cartella "${folderName}" in questa posizione.`);
    this.name = "FolderExistsError";
  }
}

/** Caratteri non ammessi nei nomi di file e cartelle di SharePoint / OneDrive. */
const INVALID = /["*:<>?/\\|]/g;

/**
 * Nome di cartella valido per SharePoint: sostituisce i caratteri non ammessi,
 * comprime gli spazi, toglie spazi e punti iniziali/finali.
 */
export function sanitizeFolderName(name: string): string {
  return name
    .replace(INVALID, "-")
    .replace(/[\u0000-\u001f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .slice(0, 250);
}

const enc = (name: string) => encodeURIComponent(name).replace(/'/g, "%27");

export class DriveFolders {
  constructor(
    private readonly graph: GraphClient,
    readonly driveId: string,
  ) {}

  /**
   * Risolve l'URL di una cartella (come appare nel browser, es.
   * https://contoso.sharepoint.com/sites/X/Modelli/CartellaGara) cercando la
   * raccolta del sito a cui appartiene.
   */
  static async fromUrl(graph: GraphClient, siteId: string, url: string): Promise<{ folders: DriveFolders; item: FolderItem }> {
    const target = normalizeUrl(url);
    const drives = await graph.getAll<{ id: string; name: string; webUrl: string }>(`/sites/${siteId}/drives?$select=id,name,webUrl`);
    const drive = drives
      .filter((d) => target === normalizeUrl(d.webUrl) || target.startsWith(`${normalizeUrl(d.webUrl)}/`))
      .sort((a, b) => b.webUrl.length - a.webUrl.length)[0];
    if (!drive) throw new Error(`Nessuna raccolta del sito contiene ${url}`);
    const folders = new DriveFolders(graph, drive.id);
    const rest = target.slice(normalizeUrl(drive.webUrl).length).replace(/^\//, "");
    const item = rest ? await folders.byPath(rest) : await folders.root();
    return { folders, item };
  }

  async root(): Promise<FolderItem> {
    return this.toFolder(await this.graph.request<RawItem>(`/drives/${this.driveId}/root?$select=${SELECT}`));
  }

  async get(id: string): Promise<FolderItem> {
    return this.toFolder(await this.graph.request<RawItem>(`/drives/${this.driveId}/items/${id}?$select=${SELECT}`));
  }

  /** Percorso relativo alla radice della raccolta (segmenti già decodificati). */
  async byPath(path: string): Promise<FolderItem> {
    const p = path.split("/").filter(Boolean).map(enc).join("/");
    if (!p) return this.root();
    return this.toFolder(await this.graph.request<RawItem>(`/drives/${this.driveId}/root:/${p}?$select=${SELECT}`));
  }

  /** Sottocartelle (tutte le pagine) e numero di file direttamente contenuti. */
  async children(id: string): Promise<{ folders: FolderItem[]; files: number }> {
    const raw = await this.graph.getAll<RawItem>(`/drives/${this.driveId}/items/${id}/children?$select=${SELECT}&$top=200`);
    const folders = raw.filter((r) => r.folder).map((r) => this.toFolder(r));
    folders.sort((a, b) => a.name.localeCompare(b.name, "it", { numeric: true, sensitivity: "base" }));
    return { folders, files: raw.filter((r) => r.file).length };
  }

  /** Sottocartelle e file (tutte le pagine), in ordine di nome. */
  async entries(id: string): Promise<{ folders: FolderItem[]; files: FileItem[] }> {
    const raw = await this.graph.getAll<RawItem>(
      `/drives/${this.driveId}/items/${id}/children?$select=${SELECT},size,lastModifiedDateTime&$top=200`,
    );
    const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "it", { numeric: true, sensitivity: "base" });
    const folders = raw.filter((r) => r.folder).map((r) => this.toFolder(r)).sort(byName);
    const files = raw
      .filter((r) => r.file)
      .map((r) => {
        const f = this.toFolder(r);
        return { id: f.id, name: f.name, webUrl: f.webUrl, path: f.path, size: r.size, modifiedAt: r.lastModifiedDateTime };
      })
      .sort(byName);
    return { folders, files };
  }

  /**
   * Crea una cartella. Se il nome è già usato lancia FolderExistsError
   * (con la cartella esistente, se è una cartella).
   */
  async create(parentId: string, name: string): Promise<FolderItem> {
    const clean = sanitizeFolderName(name);
    if (!clean) throw new Error("Nome della cartella non valido.");
    try {
      const raw = await this.graph.request<RawItem>(`/drives/${this.driveId}/items/${parentId}/children`, {
        method: "POST",
        body: { name: clean, folder: {}, "@microsoft.graph.conflictBehavior": "fail" },
      });
      return this.toFolder(raw);
    } catch (e) {
      if (!(e instanceof GraphError && e.isConflict)) throw e;
      const existing = await this.childByName(parentId, clean);
      throw new FolderExistsError(clean, existing);
    }
  }

  /** Elemento figlio per nome, se esiste ed è una cartella. */
  async childByName(parentId: string, name: string): Promise<FolderItem | undefined> {
    try {
      const raw = await this.graph.request<RawItem>(`/drives/${this.driveId}/items/${parentId}:/${enc(name)}?$select=${SELECT}`);
      return raw.folder ? this.toFolder(raw) : undefined;
    } catch (e) {
      if (e instanceof GraphError && e.isNotFound) return undefined;
      throw e;
    }
  }

  /**
   * Cerca cartelle per nome in tutta la raccolta (ricerca di SharePoint: le cartelle
   * appena create possono comparire con qualche minuto di ritardo).
   * I risultati della ricerca non sempre riportano il percorso: lo si rilegge per id.
   */
  async search(text: string, max = 30): Promise<FolderItem[]> {
    const q = encodeURIComponent(text.replace(/'/g, "''"));
    const raw = await this.graph.getAll<RawItem>(`/drives/${this.driveId}/root/search(q='${q}')?$select=${SELECT}`, { max: 300 });
    const t = text.toLowerCase();
    const hits = raw.filter((r) => r.folder && r.name.toLowerCase().includes(t)).slice(0, max);
    const res = await this.batchWithRetry(hits.map((h) => ({ url: `/drives/${this.driveId}/items/${h.id}?$select=${SELECT}` })));
    return res.filter((r) => r.status === 200).map((r) => this.toFolder(r.body as RawItem));
  }

  /** Metadati (colonne della raccolta) della cartella. */
  async fields<F = Record<string, unknown>>(id: string): Promise<F> {
    const r = await this.graph.request<{ fields: F }>(`/drives/${this.driveId}/items/${id}/listItem?$expand=fields`);
    return r.fields;
  }

  /** Aggiorna le colonne della raccolta sulla cartella (lookup come `<Nome>LookupId`). */
  async setFields(id: string, fields: Record<string, unknown>): Promise<void> {
    await this.graph.request(`/drives/${this.driveId}/items/${id}/listItem/fields`, { method: "PATCH", body: fields });
  }

  /** Conta file e cartelle contenuti, a qualsiasi profondità. */
  async countDeep(id: string): Promise<{ folders: number; files: number }> {
    let folders = 0;
    let files = 0;
    let level = [id];
    while (level.length) {
      const next: string[] = [];
      for (const parent of level) {
        const c = await this.children(parent);
        files += c.files;
        folders += c.folders.length;
        next.push(...c.folders.map((f) => f.id));
      }
      level = next;
    }
    return { folders, files };
  }

  /** Struttura delle sottocartelle (i file vengono ignorati), letta livello per livello. */
  async readTemplate(id: string): Promise<TemplateNode[]> {
    const out: TemplateNode[] = [];
    let level: Array<{ id: string; into: TemplateNode[] }> = [{ id, into: out }];
    while (level.length) {
      const next: typeof level = [];
      const res = await this.batchWithRetry(
        level.map((l) => ({ url: `/drives/${this.driveId}/items/${l.id}/children?$select=id,name,folder&$top=200` })),
      );
      for (let i = 0; i < level.length; i++) {
        const r = res[i]!;
        if (r.status !== 200) throw batchError(r, "lettura del modello");
        const body = r.body as { value: RawItem[]; "@odata.nextLink"?: string };
        let items = body.value;
        // Cartella con più di 200 elementi: si seguono le pagine successive
        let link = body["@odata.nextLink"];
        while (link) {
          const page = await this.graph.getPage<RawItem>(link);
          items = items.concat(page.items);
          link = page.nextLink;
        }
        for (const f of items.filter((x) => x.folder).sort((a, b) => a.name.localeCompare(b.name, "it", { numeric: true }))) {
          const node: TemplateNode = { name: f.name, children: [] };
          level[i]!.into.push(node);
          next.push({ id: f.id, into: node.children });
        }
      }
      level = next;
    }
    return out;
  }

  /**
   * Crea `rootName` dentro `parentId` e vi ricostruisce le sottocartelle di `template`,
   * livello per livello, con richieste $batch (20 per chiamata).
   *
   * Ripetibile: le cartelle già presenti (esecuzione precedente interrotta) vengono
   * riusate e si creano solo quelle mancanti. Se la radice esiste già, `acceptExisting`
   * decide se riusarla (es. è la cartella della stessa gara) o fermarsi.
   */
  async buildTree(opts: {
    parentId: string;
    rootName: string;
    template: TemplateNode[];
    acceptExisting?: (existing: FolderItem) => Promise<boolean>;
    onProgress?: (p: BuildProgress) => void;
  }): Promise<BuildResult> {
    const total = 1 + countNodes(opts.template);
    let done = 0;
    let created = 0;
    let reused = 0;
    const tick = () => opts.onProgress?.({ done, total });

    let root: FolderItem;
    try {
      root = await this.create(opts.parentId, opts.rootName);
      created++;
    } catch (e) {
      if (!(e instanceof FolderExistsError)) throw e;
      if (!e.existing || !opts.acceptExisting || !(await opts.acceptExisting(e.existing))) throw e;
      root = e.existing;
      reused++;
    }
    done++;
    tick();

    let level = opts.template.map((node) => ({ node, parentId: root.id }));
    while (level.length) {
      const posts = await this.batchWithRetry(
        level.map((l) => ({
          url: `/drives/${this.driveId}/items/${l.parentId}/children`,
          method: "POST" as const,
          body: { name: sanitizeFolderName(l.node.name), folder: {}, "@microsoft.graph.conflictBehavior": "fail" },
        })),
      );

      const ids: string[] = new Array(level.length);
      const conflicts: number[] = [];
      posts.forEach((r, i) => {
        if (r.status === 201 || r.status === 200) {
          ids[i] = (r.body as RawItem).id;
          created++;
        } else if (r.status === 409) conflicts.push(i);
        else throw batchError(r, `creazione di "${level[i]!.node.name}"`);
      });

      if (conflicts.length) {
        const gets = await this.batchWithRetry(
          conflicts.map((i) => ({
            url: `/drives/${this.driveId}/items/${level[i]!.parentId}:/${enc(sanitizeFolderName(level[i]!.node.name))}?$select=id,name,folder`,
          })),
        );
        gets.forEach((r, j) => {
          const i = conflicts[j]!;
          const body = r.body as RawItem | undefined;
          if (r.status !== 200 || !body?.folder)
            throw new Error(`"${level[i]!.node.name}" esiste già ma non è una cartella: rinominarlo o rimuoverlo e riprovare.`);
          ids[i] = body.id;
          reused++;
        });
      }

      done += level.length;
      tick();
      level = level.flatMap((l, i) => l.node.children.map((node) => ({ node, parentId: ids[i]! })));
    }

    return { root, created, reused };
  }

  /** $batch che ripete le singole richieste limitate dal throttling (429/503/504). */
  private async batchWithRetry(requests: BatchRequest[], attempts = 4): Promise<BatchResponse[]> {
    let res = await this.graph.batch(requests);
    for (let a = 1; a < attempts; a++) {
      const retry = res.map((r, i) => ({ r, i })).filter(({ r }) => r.status === 429 || r.status === 503 || r.status === 504);
      if (!retry.length) break;
      const wait = Math.max(...retry.map(({ r }) => Number(r.headers?.["Retry-After"] ?? r.headers?.["retry-after"]) || 2 ** a));
      await new Promise((ok) => setTimeout(ok, Math.min(wait, 30) * 1000));
      const again = await this.graph.batch(retry.map(({ i }) => requests[i]!));
      res = [...res];
      retry.forEach(({ i }, j) => (res[i] = again[j]!));
    }
    return res;
  }

  private toFolder(r: RawItem): FolderItem {
    return {
      id: r.id,
      name: r.root ? "" : r.name,
      webUrl: r.webUrl,
      path: r.root ? "" : relativePath(r.parentReference?.path, r.name),
      childCount: r.folder?.childCount ?? 0,
    };
  }
}

function countNodes(nodes: TemplateNode[]): number {
  return nodes.reduce((n, x) => n + 1 + countNodes(x.children), 0);
}

/** "/drives/{id}/root:/Comuni/A" + "Alatri" → "Comuni/A/Alatri". */
function relativePath(parentPath: string | undefined, name: string): string {
  const after = parentPath?.split("root:")[1] ?? "";
  const parent = decodeURIComponent(after).replace(/^\/+|\/+$/g, "");
  return parent ? `${parent}/${name}` : name;
}

function normalizeUrl(u: string): string {
  try {
    const x = new URL(u);
    // Indirizzo copiato dalla vista della raccolta: …/Forms/AllItems.aspx?id=/sites/X/Raccolta/Cartella
    const id = x.searchParams.get("id");
    const path = id && id.startsWith("/") ? id : decodeURIComponent(x.pathname);
    return `${x.origin}${path}`.replace(/\/+$/, "").toLowerCase();
  } catch {
    return u.replace(/\/+$/, "").toLowerCase();
  }
}

function batchError(r: BatchResponse, what: string): GraphError {
  const err = (r.body as { error?: { code?: string; message?: string } } | undefined)?.error;
  return new GraphError(r.status, err?.code ?? `HTTP${r.status}`, `${what}: ${err?.message ?? "errore"}`);
}
